import { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Package } from "lucide-react";
import MissionShell from "../MissionShell";
import { Card, Field, Btn } from "@/components/kit";
import { CATEGORIES, PRODUCT_EXAMPLE } from "../content";
import { useProgress } from "../store";
import { isDone, REQUIREMENTS, filled } from "../logic";

const FIELDS = [
  ["why", "Pourquoi je l'ai choisi", "Ex. : je l'utilise tous les jours, il est simple à montrer…"],
  ["utility", "Son utilité principale", "À quoi sert-il concrètement ?"],
  ["detail", "Le détail que je veux montrer", "Une texture, une fermeture, une forme, un rangement…"],
];

function ExamplePanel() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-dashed border-gold/60 bg-gold-soft/30 p-4">
      <Btn variant="ghost" size="sm" onClick={() => setOpen(!open)} data-testid="toggle-product-example" className="-ml-2">
        {open ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}{open ? "Masquer l'exemple" : "Voir un exemple rempli"}
      </Btn>
      {open && (
        <dl className="mt-3 space-y-2 text-sm" data-testid="product-example-panel">
          <p className="text-xs text-soft">Exemple en lecture seule : il ne modifie pas tes réponses.</p>
          {[["Nom", PRODUCT_EXAMPLE.name], ["Catégorie", PRODUCT_EXAMPLE.category], ["Pourquoi", PRODUCT_EXAMPLE.why], ["Utilité", PRODUCT_EXAMPLE.utility], ["Détail", PRODUCT_EXAMPLE.detail]].map(([k, v]) => (
            <div key={k} className="grid grid-cols-[90px_1fr] gap-3"><dt className="font-semibold text-bx-2">{k}</dt><dd className="text-ink/85">{v}</dd></div>
          ))}
        </dl>
      )}
    </div>
  );
}

export default function Mission2() {
  const { state, update, setCompleted } = useProgress();
  const p = state.m2;
  const set = (k) => (v) => update("m2", { [k]: v });
  const missing = REQUIREMENTS[2](state);
  return (
    <MissionShell id={2} done={isDone(state, 2)} validate={{ missing, onClick: () => { setCompleted(2, true); toast.success("Mission 02 validée !"); } }}>
      <div className="space-y-5">
        <ExamplePanel />
        <Card className="space-y-5">
          <Field label="Nom du produit" value={p.name} onChange={set("name")} placeholder="Ex. : ma gourde isotherme" testId="product-name-input" />
          <div className="space-y-2">
            <p className="text-[15px] font-medium text-ink">Catégorie</p>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Catégorie">
              {CATEGORIES.map((c, i) => (
                <button key={c} type="button" role="radio" aria-checked={p.category === c} onClick={() => set("category")(c)} data-testid={`product-category-${i}`}
                  className={`min-h-[40px] rounded-full border px-4 text-sm transition-colors ${p.category === c ? "border-bx bg-bx text-white" : "border-line bg-white text-ink hover:border-bx/40"}`}>
                  {c}
                </button>
              ))}
            </div>
          </div>
          {FIELDS.map(([k, label, ph]) => <Field key={k} label={label} value={p[k]} onChange={set(k)} placeholder={ph} multiline rows={2} testId={`product-${k}-input`} />)}
          <p className="text-xs text-soft">Enregistrement automatique dans ton compte.</p>
        </Card>
        <div className="rounded-[20px] bg-bx p-6 text-white sm:p-7" data-testid="product-summary-card">
          <div className="flex items-center gap-3">
            <Package className="h-5 w-5 text-gold" strokeWidth={1.5} />
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Mon produit UGC</p>
          </div>
          <p className="mt-4 font-display text-2xl">{filled(p.name) ? p.name : "Ton produit apparaîtra ici"}</p>
          {filled(p.category) && <span className="mt-2 inline-block rounded-full bg-white/10 px-3 py-1 text-xs">{p.category}</span>}
          <dl className="mt-4 space-y-2 text-sm text-white/85">
            {filled(p.why) && <div><dt className="inline font-semibold text-white">Pourquoi : </dt><dd className="inline">{p.why}</dd></div>}
            {filled(p.utility) && <div><dt className="inline font-semibold text-white">Utilité : </dt><dd className="inline">{p.utility}</dd></div>}
            {filled(p.detail) && <div><dt className="inline font-semibold text-white">Détail à montrer : </dt><dd className="inline">{p.detail}</dd></div>}
          </dl>
        </div>
      </div>
    </MissionShell>
  );
}
